"use client"

import { Link } from "@tanstack/react-router"
import { Loader2, Search } from "lucide-react"

import { RecordTabs } from "@/components/graphite/record-tabs"
import { SelectChip } from "@/components/graphite/select-chip"
import { stageHex } from "@/lib/stage-colors"
import { cn } from "@/lib/utils"
import { JOB_TABS, type JobRow, type JobTab } from "@/hooks/use-jobs"

/**
 * The jobs list.
 *
 * Turn 9 has no artboard for it — 4a is one job's console with the Jobs tab
 * lit — so this is 5a's table, which is the same kind of screen: a page of
 * records under a search, a filter and a count. The counts read mono the way
 * 4a's readout strip does, and status is a dot and a lowercase word rather than
 * a pill.
 *
 * Purely presentational; everything arrives from `useJobs`.
 */

/*
  A job's status has no colour of its own in the canvas, so it borrows the
  stage ramp — which is the app's one semantic ramp and already has a light and
  a dark version, so a status keeps its meaning through a theme switch.
*/
const STATUS_STAGE: Record<string, string> = {
  active: "hired",
  paused: "screening",
  closed: "longlist",
  draft: "longlist",
}

const GRID =
  "grid grid-cols-[minmax(0,2fr)_minmax(0,1fr)_minmax(0,0.8fr)_minmax(0,0.5fr)_minmax(0,0.5fr)_minmax(0,0.8fr)_minmax(0,0.7fr)] items-center gap-4 px-4 @2xl:px-[18px]"

function Status({ status }: { status: string }) {
  const hex = stageHex(STATUS_STAGE[status] ?? "longlist")
  return (
    <span className="inline-flex items-center gap-2">
      <span
        aria-hidden
        className="size-1.5 flex-none rounded-full"
        style={{ backgroundColor: hex }}
      />
      <span className="truncate font-mono text-[11px]" style={{ color: hex }}>
        {status}
      </span>
    </span>
  )
}

function Row({ row }: { row: JobRow }) {
  const hex = stageHex(STATUS_STAGE[row.status] ?? "longlist")

  return (
    <>
      {/* Wide: 5a's table. */}
      <Link
        to="/jobs/$jobId/$view"
        params={{ jobId: row.id, view: "overview" }}
        className={cn(GRID, "hidden w-full border-t py-3 transition-colors hover:bg-accent @2xl:grid")}
      >
        <span className="min-w-0">
          <span className="block truncate text-[12.5px] font-medium">{row.title}</span>
          <span className="mt-[3px] block truncate font-mono text-[11px] text-muted-foreground">
            {row.subtitle}
          </span>
        </span>
        <span className="truncate text-[12px] text-muted-foreground">{row.location}</span>
        <span className="truncate font-mono text-[11px] text-muted-foreground">
          {row.type}
        </span>
        <span className="text-right font-mono text-[12px]">{row.applicants}</span>
        <span className="text-right font-mono text-[12px]">{row.interviews}</span>
        <Status status={row.status} />
        <span className="text-right font-mono text-[11px] text-muted-foreground">
          {row.opened ?? "—"}
        </span>
      </Link>

      {/* Narrow: the same record as two lines and a status, per 8g's pattern. */}
      <Link
        to="/jobs/$jobId/$view"
        params={{ jobId: row.id, view: "overview" }}
        className="flex w-full items-start gap-3 border-t px-4 py-3.5 transition-colors hover:bg-accent @2xl:hidden"
      >
        <span
          aria-hidden
          className="mt-1.5 size-1.5 flex-none rounded-full"
          style={{ backgroundColor: hex }}
        />
        <span className="min-w-0 flex-1">
          <span className="block truncate text-[13.5px] font-medium">{row.title}</span>
          <span className="mt-1 block truncate font-mono text-[11px] text-muted-foreground">
            {row.subtitle}
          </span>
          <span className="mt-1.5 flex flex-wrap gap-x-3 font-mono text-[10.5px] text-muted-foreground">
            <span>{row.location}</span>
            <span>{row.type}</span>
            <span>{row.applicants} applicants</span>
          </span>
        </span>
      </Link>
    </>
  )
}

export function GraphiteJobs({
  rows,
  locations,
  search,
  onSearchChange,
  location,
  onLocationChange,
  total,
  isLoading,
  isError,
}: {
  rows: JobRow[]
  locations: string[]
  search: string
  onSearchChange: (next: string) => void
  location: string
  onLocationChange: (next: string) => void
  total: number
  isLoading: boolean
  isError: boolean
}) {
  return (
    <div className="flex min-h-0 flex-1 flex-col gap-3 px-4 pb-4 pt-3.5 @2xl:gap-3.5 @2xl:px-[26px] @2xl:pb-[26px] @2xl:pt-[18px]">
      <div className="flex flex-none flex-wrap items-center gap-2 @2xl:gap-2">
        <span className="relative inline-flex min-w-0 flex-1 items-center gap-2 rounded-md border bg-card px-3 py-[9px] @2xl:mr-1 @2xl:w-[248px] @2xl:flex-none">
          <Search className="size-3.5 flex-none text-muted-foreground" />
          <input
            value={search}
            onChange={(event) => onSearchChange(event.target.value)}
            placeholder="Search jobs"
            aria-label="Search jobs by title, company or location"
            className="min-w-0 flex-1 bg-transparent text-[12.5px] outline-none placeholder:text-muted-foreground"
          />
        </span>

        {/*
          The shared listbox, not a transparent `select` laid over a picture of
          one — and shown at every width. Hidden below @2xl, the only way to
          narrow a long list on a phone was to type into the search.
        */}
        <SelectChip
          options={[
            { value: "all", label: "All locations" },
            ...locations.map((name) => ({ value: name, label: name })),
          ]}
          value={location}
          onChange={onLocationChange}
          listLabel="Filter by location"
          active={location !== "all"}
          mono={false}
          className="flex-none"
          triggerClassName="max-w-[200px] py-2"
        />

        {/*
          What the filters left. It was gated at @2xl, so the one width where a
          list is mostly off-screen was the width that would not say how much of
          it there is — and then, once it was always on screen, it said
          "0 jobs · 0 shown" underneath the words "Loading jobs…". A count of an
          unread list is not a small count, it is a wrong one.
        */}
        {!isLoading && !isError && (
          <span className="w-full whitespace-nowrap text-[11.5px] text-muted-foreground @2xl:ml-auto @2xl:w-auto">
            {total} job{total === 1 ? "" : "s"} · {rows.length} shown
          </span>
        )}
      </div>

      {/*
        The table is a surface and carries one at every width. The border and
        the radius used to arrive at @2xl, and the ATS pane is under that most
        of the time — so the list ran edge to edge on a flat ground and the page
        read as unfinished rather than as a card.
      */}
      <div className="flex min-h-0 flex-1 flex-col overflow-hidden rounded-[10px] border bg-card">
        <div
          className={cn(
            GRID,
            "hidden shrink-0 border-b py-2.5 text-[11.5px] font-medium text-muted-foreground @2xl:grid",
          )}
        >
          <div>Job</div>
          <div>Location</div>
          <div>Type</div>
          <div className="text-right">Applicants</div>
          <div className="text-right">Interviews</div>
          <div>Status</div>
          <div className="text-right">Opened</div>
        </div>

        <div className="min-h-0 flex-1 overflow-y-auto">
          {isLoading && (
            <div className="flex items-center gap-2 p-4 text-[12.5px] text-muted-foreground">
              <Loader2 className="size-3.5 animate-spin" />
              Loading jobs…
            </div>
          )}

          {isError && (
            <p className="p-4 text-[12.5px] text-muted-foreground">
              Jobs could not be loaded.
            </p>
          )}

          {!isLoading && !isError && rows.length === 0 && (
            <p className="p-6 text-[12.5px] text-muted-foreground">
              {total === 0
                ? "No jobs yet. Configure one to start a pipeline."
                : "No jobs match these filters."}
            </p>
          )}

          {rows.map((row) => (
            <Row key={row.id} row={row} />
          ))}
        </div>
      </div>
    </div>
  )
}

/** The four statuses as tabs, with what each holds. Lives in the page header. */
export function JobTabs({
  tab,
  counts,
  onTabChange,
}: {
  tab: JobTab
  /** Absent until the jobs have been read — a tab then shows only its name. */
  counts?: Record<JobTab, number>
  onTabChange: (next: JobTab) => void
}) {
  return <RecordTabs tabs={JOB_TABS} value={tab} counts={counts} onChange={onTabChange} />
}

